import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ComisionesLayOut } from 'src/app/model/commission-layout/comisiones-layout';
import { CommissionLayoutService } from 'src/app/services/commission-layout.service';

interface DocumentCard {
  id: number;
  nombreDocumento: string;
  fechaCarga: Date;
  fechaInicial: Date;
  fechaFinal: Date;
  status: 'pendiente' | 'enviado';
  cifrasControl: {
    totalPago: number;
    totalPagoValidos: number;
    colocacion: number;
    comision: number;
  };
}

@Component({
  selector: 'app-comisiones-documentos',
  templateUrl: './comisiones-documentos.component.html',
  styleUrls: ['./comisiones-documentos.component.css']
})
export class ComisionesDocumentosComponent implements OnInit {

  documentos: DocumentCard[] = [];

  // Variables para filtrado, ordenamiento y paginación
  searchTerm: string = '';
  selectedStatus: string = '';
  sortBy: string = 'fechaCarga';
  sortDirection: 'asc' | 'desc' = 'desc';
  currentPage: number = 1;
  itemsPerPage: number = 8;
  
  // Datos procesados
  filteredDocuments: DocumentCard[] = [];
  paginatedDocuments: DocumentCard[] = [];
  totalPages: number = 0;

  statusOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'enviado', label: 'Enviado' }
  ];

  constructor(private router: Router,
    private commissionLayoutService: CommissionLayoutService
  ) {}

  ngOnInit(): void {
    this.findAllDocuments();
  }

  findAllDocuments() {
    this.commissionLayoutService.getAllDocuments().subscribe(
      (documents: ComisionesLayOut[]) => {
        console.log('Documentos obtenidos:', documents);

        this.documentos = documents.map(doc => ({
          id: doc.idComisionesLayOut,
          nombreDocumento: doc.nombreDocumento,
          fechaCarga: new Date(doc.createDate),
          fechaInicial: new Date(doc.fechaInicial + 'T00:00:00'),
          fechaFinal: new Date(doc.fechaFinal + 'T00:00:00'),
          status: doc.status === 0 ? 'pendiente' : 'enviado',
          cifrasControl: {
            totalPago: doc.totalPagosUp,
            totalPagoValidos: doc.pagosValidadosUp,
            colocacion: doc.capitalUp,
            comision: doc.comisionesUp
          }
        }));

        // Aplicar filtros una vez tengamos la data
        this.applyFiltersAndSort();
      },
      (error) => {
        console.error('Error al obtener documentos:', error);
      }
    );
  }

  // Aplicar filtros, ordenamiento y paginación
  applyFiltersAndSort(): void {
    let filtered = [...this.documentos];

    // Filtrar por término de búsqueda
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.nombreDocumento.toLowerCase().includes(term)
      );
    }

    // Filtrar por status
    if (this.selectedStatus) {
      filtered = filtered.filter(doc => doc.status === this.selectedStatus);
    }

    // Ordenar
    filtered.sort((a, b) => {
      let comparison = 0;
      
      if (this.sortBy === 'fechaCarga') {
        comparison = a.fechaCarga.getTime() - b.fechaCarga.getTime();
      } else if (this.sortBy === 'status') {
        comparison = a.status.localeCompare(b.status);
      }
      
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.filteredDocuments = filtered;
    this.totalPages = Math.ceil(this.filteredDocuments.length / this.itemsPerPage);
    
    // Ajustar página actual si es necesario
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    } else if (this.currentPage < 1) {
      this.currentPage = 1;
    }

    this.updatePagination();
  }

  // Actualizar paginación
  updatePagination(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedDocuments = this.filteredDocuments.slice(startIndex, endIndex);
  }

  // Eventos de filtrado
  onSearchChange(): void {
    this.currentPage = 1;
    this.applyFiltersAndSort();
  }

  onStatusChange(): void {
    this.currentPage = 1;
    this.applyFiltersAndSort();
  }

  onSortChange(): void {
    this.applyFiltersAndSort();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.applyFiltersAndSort();
  }

  // Eventos de paginación
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  previousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  // Obtener páginas para mostrar en paginador
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, this.currentPage - halfVisible);
    let endPage = Math.min(this.totalPages, this.currentPage + halfVisible);
    
    // Ajustar si estamos cerca del inicio o final
    if (endPage - startPage < maxVisiblePages - 1) {
      if (startPage === 1) {
        endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
      } else {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  // TrackBy function para optimización
  trackByDocumento(index: number, documento: DocumentCard): number {
    return documento.id;
  }

  getStatusClass(status: string): string {
    const statusClasses = {
      'pendiente': 'badge-warning',
      'enviado': 'badge-success'
    };
    return statusClasses[status] || 'badge-secondary';
  }

  getStatusText(status: string): string {
    const statusTexts = {
      'pendiente': 'Pendiente',
      'enviado': 'Enviado'
    };
    return statusTexts[status] || status;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }

  viewDetails(documento: DocumentCard): void {
    console.log('Ver detalles del documento:', documento);
    this.router.navigateByUrl(`nomina/envioComisiones/${documento.id}`);
  }

}